import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { 
  Lock, LogOut, Users, MessageSquare, Search, Filter, 
  Check, Clock, Phone, Mail, MapPin, Building2, Trash2,
  RefreshCcw, AlertCircle, HelpCircle, ChevronDown, X
} from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ASSETS = {
  logoWhite: "https://customer-assets.emergentagent.com/job_venue-revenue/artifacts/rcizesv6_AnytimeFumeWhiteLOGO.png",
};

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [activeTab, setActiveTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterVenueType, setFilterVenueType] = useState("all");
  const [isLoading, setIsLoading] = useState(false);

  // Check if already authenticated (session storage)
  useEffect(() => {
    const auth = sessionStorage.getItem("adminAuth");
    if (auth === "true") {
      setIsAuthenticated(true);
    }
  }, []);

  // Fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [leadsRes, ticketsRes, statsRes] = await Promise.all([
        axios.get(`${API}/leads`),
        axios.get(`${API}/support`),
        axios.get(`${API}/stats`)
      ]);
      setLeads(leadsRes.data);
      setTickets(ticketsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    try {
      await axios.post(`${API}/admin/login`, { password });
      setIsAuthenticated(true);
      sessionStorage.setItem("adminAuth", "true");
      toast.success("Login successful");
    } catch (error) {
      toast.error("Invalid password");
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem("adminAuth");
  };

  const updateLeadStatus = async (leadId, status) => {
    try {
      await axios.patch(`${API}/leads/${leadId}`, { status });
      setLeads(leads.map(l => l.id === leadId ? { ...l, status } : l));
      toast.success("Lead updated");
    } catch (error) {
      toast.error("Failed to update lead");
    }
  };

  const updateTicketStatus = async (ticketId, status) => {
    try {
      await axios.patch(`${API}/support/${ticketId}`, { status });
      setTickets(tickets.map(t => t.id === ticketId ? { ...t, status } : t));
      toast.success("Ticket updated");
    } catch (error) {
      toast.error("Failed to update ticket");
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;
    try {
      await axios.delete(`${API}/leads/${leadId}`);
      setLeads(leads.filter(l => l.id !== leadId));
      toast.success("Lead deleted");
    } catch (error) {
      toast.error("Failed to delete lead");
    }
  };

  const deleteTicket = async (ticketId) => {
    if (!window.confirm("Are you sure you want to delete this ticket?")) return;
    try {
      await axios.delete(`${API}/support/${ticketId}`);
      setTickets(tickets.filter(t => t.id !== ticketId));
      toast.success("Ticket deleted");
    } catch (error) {
      toast.error("Failed to delete ticket");
    }
  };

  // Filter leads
  const filteredLeads = leads.filter(lead => {
    const matchesSearch = 
      lead.venue_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.city.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === "all" || lead.status === filterStatus;
    const matchesVenueType = filterVenueType === "all" || lead.venue_type === filterVenueType;
    return matchesSearch && matchesStatus && matchesVenueType;
  });

  // Filter tickets
  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = 
      ticket.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.machine_location?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === "all" || ticket.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getIssueIcon = (type) => {
    switch (type) {
      case "refund": return <RefreshCcw className="w-4 h-4" />;
      case "spray_issue": return <AlertCircle className="w-4 h-4" />;
      default: return <HelpCircle className="w-4 h-4" />;
    }
  };

  const getIssueLabel = (type) => {
    switch (type) {
      case "refund": return "Refund Request";
      case "spray_issue": return "Spray Issue";
      default: return "Other";
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6">
        <Toaster position="top-center" toastOptions={{ style: { background: '#0a0a0a', color: '#fff', border: '1px solid rgba(255,255,255,0.1)' }}} />
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <img src={ASSETS.logoWhite} alt="Anytime Fume" className="h-12 mx-auto mb-4" />
            <h1 className="font-heading text-3xl text-white mb-2">Admin Dashboard</h1>
            <p className="font-body text-white/50 text-sm">Enter your password to continue</p>
          </div>
          
          <form onSubmit={handleLogin} className="border border-white/10 p-8 bg-white/[0.02]">
            <div className="mb-6">
              <label className="block font-body text-white/60 text-xs uppercase tracking-wider mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-black border border-white/20 focus:border-white pl-12 pr-4 py-3 text-white focus:outline-none transition-colors font-body"
                  placeholder="Enter admin password"
                  data-testid="admin-password-input"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={isLoggingIn}
              data-testid="admin-login-btn"
              className="w-full bg-white text-black py-3 text-sm font-medium tracking-wider uppercase hover:bg-white/90 transition-all disabled:opacity-50"
            >
              {isLoggingIn ? "Logging in..." : "Login"}
            </button>
          </form>
          
          <p className="text-center mt-6 text-white/30 text-xs font-body">
            <a href="/" className="hover:text-white/60">← Back to Website</a>
          </p>
        </motion.div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-black">
      <Toaster position="top-center" toastOptions={{ style: { background: '#0a0a0a', color: '#fff', border: '1px solid rgba(255,255,255,0.1)' }}} />
      
      {/* Header */}
      <header className="border-b border-white/10 sticky top-0 bg-black/90 backdrop-blur-lg z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={ASSETS.logoWhite} alt="Anytime Fume" className="h-8" />
            <span className="font-body text-white/60 text-sm">Admin Dashboard</span>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-white/50 hover:text-white transition-colors text-sm font-body"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="border border-white/10 p-6 bg-white/[0.02]">
              <p className="font-body text-white/40 text-xs uppercase tracking-wider mb-1">Total Leads</p>
              <p className="font-heading text-3xl text-white">{stats.leads.total}</p>
            </div>
            <div className="border border-white/10 p-6 bg-white/[0.02]">
              <p className="font-body text-white/40 text-xs uppercase tracking-wider mb-1">New Leads</p>
              <p className="font-heading text-3xl text-green-400">{stats.leads.new}</p>
            </div>
            <div className="border border-white/10 p-6 bg-white/[0.02]">
              <p className="font-body text-white/40 text-xs uppercase tracking-wider mb-1">Support Tickets</p>
              <p className="font-heading text-3xl text-white">{stats.support.total}</p>
            </div>
            <div className="border border-white/10 p-6 bg-white/[0.02]">
              <p className="font-body text-white/40 text-xs uppercase tracking-wider mb-1">Open Tickets</p>
              <p className="font-heading text-3xl text-yellow-400">{stats.support.open}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-white/10">
          <button
            onClick={() => { setActiveTab("leads"); setSearchTerm(""); setFilterStatus("all"); }}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-body transition-colors border-b-2 -mb-[1px] ${
              activeTab === "leads" 
                ? "text-white border-white" 
                : "text-white/50 border-transparent hover:text-white/80"
            }`}
          >
            <Users className="w-4 h-4" />
            Venue Leads ({leads.length})
          </button>
          <button
            onClick={() => { setActiveTab("tickets"); setSearchTerm(""); setFilterStatus("all"); }}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-body transition-colors border-b-2 -mb-[1px] ${
              activeTab === "tickets" 
                ? "text-white border-white" 
                : "text-white/50 border-transparent hover:text-white/80"
            }`}
          >
            <MessageSquare className="w-4 h-4" />
            Support Tickets ({tickets.length})
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              className="w-full bg-black border border-white/20 pl-10 pr-4 py-2 text-white text-sm focus:outline-none focus:border-white/40 font-body"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-black border border-white/20 px-4 py-2 text-white text-sm focus:outline-none focus:border-white/40 font-body"
          >
            <option value="all">All Status</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="open">Open</option>
            <option value="resolved">Resolved</option>
          </select>
          {activeTab === "leads" && (
            <select
              value={filterVenueType}
              onChange={(e) => setFilterVenueType(e.target.value)}
              className="bg-black border border-white/20 px-4 py-2 text-white text-sm focus:outline-none focus:border-white/40 font-body"
            >
              <option value="all">All Venues</option>
              <option value="gym">Gym</option>
              <option value="nightclub">Nightclub</option>
              <option value="office">Office</option>
              <option value="shopping">Shopping Mall</option>
              <option value="university">University</option>
              <option value="residential">Residential</option>
            </select>
          )}
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 border border-white/20 text-white/70 hover:text-white hover:border-white/40 transition-colors text-sm font-body"
          >
            <RefreshCcw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Leads Table */}
        {activeTab === "leads" && (
          <div className="border border-white/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left px-4 py-3 text-white/60 text-xs uppercase tracking-wider font-body">Venue</th>
                    <th className="text-left px-4 py-3 text-white/60 text-xs uppercase tracking-wider font-body">Contact</th>
                    <th className="text-left px-4 py-3 text-white/60 text-xs uppercase tracking-wider font-body">Type</th>
                    <th className="text-left px-4 py-3 text-white/60 text-xs uppercase tracking-wider font-body">City</th>
                    <th className="text-left px-4 py-3 text-white/60 text-xs uppercase tracking-wider font-body">Date</th>
                    <th className="text-left px-4 py-3 text-white/60 text-xs uppercase tracking-wider font-body">Status</th>
                    <th className="text-left px-4 py-3 text-white/60 text-xs uppercase tracking-wider font-body">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map((lead) => (
                    <tr key={lead.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-4">
                        <p className="font-body text-white text-sm">{lead.venue_name}</p>
                        <p className="font-body text-white/50 text-xs">{lead.name}</p>
                      </td>
                      <td className="px-4 py-4">
                        <a href={`mailto:${lead.email}`} className="font-body text-white/70 text-sm hover:text-white flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {lead.email}
                        </a>
                        <a href={`tel:${lead.phone}`} className="font-body text-white/50 text-xs flex items-center gap-1 mt-1">
                          <Phone className="w-3 h-3" /> {lead.phone}
                        </a>
                      </td>
                      <td className="px-4 py-4">
                        <span className="font-body text-white/60 text-sm capitalize">{lead.venue_type}</span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="font-body text-white/60 text-sm">{lead.city}</span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="font-body text-white/50 text-xs">{formatDate(lead.created_at)}</span>
                      </td>
                      <td className="px-4 py-4">
                        <select
                          value={lead.status}
                          onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                          className={`bg-transparent border text-xs px-2 py-1 font-body focus:outline-none ${
                            lead.status === "new" 
                              ? "border-green-500/50 text-green-400" 
                              : lead.status === "contacted"
                              ? "border-blue-500/50 text-blue-400"
                              : "border-white/20 text-white/60"
                          }`}
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="qualified">Qualified</option>
                          <option value="closed">Closed</option>
                        </select>
                      </td>
                      <td className="px-4 py-4">
                        <button
                          onClick={() => deleteLead(lead.id)}
                          className="text-white/30 hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredLeads.length === 0 && (
              <div className="text-center py-12 text-white/40 font-body">
                No leads found
              </div>
            )}
          </div>
        )}

        {/* Support Tickets Table */}
        {activeTab === "tickets" && (
          <div className="border border-white/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left px-4 py-3 text-white/60 text-xs uppercase tracking-wider font-body">Issue</th>
                    <th className="text-left px-4 py-3 text-white/60 text-xs uppercase tracking-wider font-body">Contact</th>
                    <th className="text-left px-4 py-3 text-white/60 text-xs uppercase tracking-wider font-body">Location</th>
                    <th className="text-left px-4 py-3 text-white/60 text-xs uppercase tracking-wider font-body">Message</th>
                    <th className="text-left px-4 py-3 text-white/60 text-xs uppercase tracking-wider font-body">Date</th>
                    <th className="text-left px-4 py-3 text-white/60 text-xs uppercase tracking-wider font-body">Status</th>
                    <th className="text-left px-4 py-3 text-white/60 text-xs uppercase tracking-wider font-body">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTickets.map((ticket) => (
                    <tr key={ticket.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <span className={`${
                            ticket.issue_type === "refund" ? "text-yellow-400" :
                            ticket.issue_type === "spray_issue" ? "text-red-400" : "text-white/60"
                          }`}>
                            {getIssueIcon(ticket.issue_type)}
                          </span>
                          <span className="font-body text-white text-sm">{getIssueLabel(ticket.issue_type)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <p className="font-body text-white/70 text-sm">{ticket.name}</p>
                        <a href={`mailto:${ticket.email}`} className="font-body text-white/50 text-xs hover:text-white">
                          {ticket.email}
                        </a>
                        <a href={`tel:${ticket.phone}`} className="font-body text-white/40 text-xs block">
                          {ticket.phone}
                        </a>
                      </td>
                      <td className="px-4 py-4">
                        <span className="font-body text-white/60 text-sm">
                          {ticket.machine_location || "-"}
                        </span>
                      </td>
                      <td className="px-4 py-4 max-w-xs">
                        <p className="font-body text-white/60 text-sm truncate" title={ticket.message}>
                          {ticket.message}
                        </p>
                      </td>
                      <td className="px-4 py-4">
                        <span className="font-body text-white/50 text-xs">{formatDate(ticket.created_at)}</span>
                      </td>
                      <td className="px-4 py-4">
                        <select
                          value={ticket.status}
                          onChange={(e) => updateTicketStatus(ticket.id, e.target.value)}
                          className={`bg-transparent border text-xs px-2 py-1 font-body focus:outline-none ${
                            ticket.status === "open" 
                              ? "border-yellow-500/50 text-yellow-400" 
                              : ticket.status === "resolved"
                              ? "border-green-500/50 text-green-400"
                              : "border-white/20 text-white/60"
                          }`}
                        >
                          <option value="open">Open</option>
                          <option value="in_progress">In Progress</option>
                          <option value="resolved">Resolved</option>
                        </select>
                      </td>
                      <td className="px-4 py-4">
                        <button
                          onClick={() => deleteTicket(ticket.id)}
                          className="text-white/30 hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredTickets.length === 0 && (
              <div className="text-center py-12 text-white/40 font-body">
                No support tickets found
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPage;
